import { Notify, SessionStorage } from 'quasar';
import { route } from 'quasar/wrappers';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';
import { DlaClient } from 'src/services/dla-api';
import { getClientIanaTimezone } from 'src/utils/timezone';
import {
  isChunkLoadError,
  CHUNK_RELOAD_ATTEMPTED_KEY,
  CHUNK_RELOAD_IN_PROGRESS_KEY,
} from 'src/utils/chunkErrorDetection';
import routes from './routes';

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */
export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(
      process.env.MODE === 'ssr' ? void 0 : process.env.VUE_ROUTER_BASE,
    ),
  });

  // Runtime-only guard for stale app shells causing chunk load failures.
  // Catches dynamic import errors during navigation and triggers a single
  // full reload per session to fetch the latest assets.

  Router.onError((err) => {
    if (!isChunkLoadError(err)) {
      console.error(err);
      return;
    }

    const attempted = SessionStorage.getItem(CHUNK_RELOAD_ATTEMPTED_KEY);
    const inProgress = SessionStorage.getItem(CHUNK_RELOAD_IN_PROGRESS_KEY);

    // Reload only once per session; prevent loops with an "in progress" flag.
    if (!attempted && !inProgress) {
      SessionStorage.set(CHUNK_RELOAD_ATTEMPTED_KEY, true);
      SessionStorage.set(CHUNK_RELOAD_IN_PROGRESS_KEY, true);
      // Force a full page reload to pull fresh index.html and chunks.
      globalThis.location?.reload();
    } else {
      // Already attempted reload this session; avoid infinite loops.
      Notify.create({
        type: 'negative',
        message:
          'The app needs an update. Please refresh the page to continue.',
      });
    }
  });

  // Clear the "in-progress" guard after any successful navigation.
  Router.afterEach((to, from, failure) => {
    if (!failure) {
      SessionStorage.remove(CHUNK_RELOAD_IN_PROGRESS_KEY);
    }
  });

  Router.beforeEach(async (to) => {
    if (to.query.code && to.query.email) {
      await new DlaClient()
        .post('/users/login', {
          email: to.query.email,
          code: to.query.code,
          iana_zone_id: getClientIanaTimezone(),
        })
        .then((user) => {
          SessionStorage.set('user', user);
          Router.replace({ path: to.path, query: undefined });
        })
        .catch(() => {
          Router.replace({ path: '/login', query: { expired: 1 } });
        });
    }
  });

  return Router;
});
